/**
 * Host Global - Property Owner Financial Wallet Dashboard
 * Comprehensive financial overview with wallet balance, revenue analytics, and transaction history
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

// Chart components (using react-chartjs-2)
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PropertyOwnerFooter from "examples/PropertyOwnerFooter";

// API
import { propertyOwnerAPI } from "utils/api";

// Icons
import Icon from "@mui/material/Icon";

function WalletDashboard() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [reservationsCount, setReservationsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Filters
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    propertyId: "all",
    type: "all",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTransactions, setTotalTransactions] = useState(0);

  // Fetch wallet and transactions
  useEffect(() => {
    fetchWallet();
    fetchProperties();
    fetchReservationsCount();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [filters, page, rowsPerPage]);

  const fetchWallet = async () => {
    try {
      setLoading(true);
      const response = await propertyOwnerAPI.getWallet();
      // Backend returns { message, wallet: {...} }
      setWallet(response.wallet || response.data || {});
    } catch (error) {
      console.error("Error fetching wallet:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to load wallet information",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await propertyOwnerAPI.getMyProperties();
      setProperties(response.data || []);
    } catch (error) {
      console.error("Error fetching properties:", error);
    }
  };

  const fetchReservationsCount = async () => {
    try {
      const response = await propertyOwnerAPI.getReservations();
      setReservationsCount((response.data || []).length);
    } catch (error) {
      // silent (wallet page still works without this)
      setReservationsCount(0);
    }
  };

  const fetchTransactions = async () => {
    try {
      setTransactionsLoading(true);
      const response = await propertyOwnerAPI.getTransactions({
        ...filters,
        page: page + 1,
        limit: rowsPerPage,
      });
      setTransactions(response.data || []);
      setTotalTransactions(response.total || 0);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to load transactions",
      });
    } finally {
      setTransactionsLoading(false);
    }
  };

  // Calculate monthly revenue from transactions
  const monthlyRevenue = useMemo(() => {
    const revenueMap = {};
    transactions
      .filter((t) => t.type === "revenue" && t.status === "completed")
      .forEach((transaction) => {
        const month = new Date(transaction.created_at).toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });
        if (!revenueMap[month]) {
          revenueMap[month] = 0;
        }
        revenueMap[month] += transaction.amount;
      });

    return Object.entries(revenueMap)
      .map(([month, revenue]) => ({ month, revenue }))
      .sort((a, b) => new Date(a.month) - new Date(b.month))
      .slice(-6); // Last 6 months
  }, [transactions]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EGP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "approved":
        return "info";
      case "pending":
        return "warning";
      default:
        return "secondary";
    }
  };

  // Get type color
  const getTypeColor = (type) => {
    switch (type) {
      case "revenue":
        return "success";
      case "fee":
        return "warning";
      case "deduction":
        return "error";
      case "cancelled":
        return "secondary";
      default:
        return "info";
    }
  };

  if (loading && !wallet) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <PropertyOwnerFooter />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header */}
        <MDBox mb={4}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => navigate("/property-owner/my-properties")}
            startIcon={<Icon>arrow_back</Icon>}
            sx={{ mb: 2 }}
          >
            Back to Properties
          </MDButton>
          <MDTypography variant="h4" fontWeight="bold" mb={1}>
            Financial Wallet
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            View your earnings, deductions, and transaction history
          </MDTypography>
        </MDBox>

        {/* Message Alert */}
        {message.text && (
          <Alert
            severity={message.type === "error" ? "error" : "success"}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Wallet Cards (only show when there is at least one reservation) */}
          {reservationsCount > 0 ? (
            <>
              {/* All 5 cards in one row - using Box with flexbox for proper 5-column layout */}
              <Grid item xs={12}>
                <MDBox
                  display="flex"
                  flexWrap="wrap"
                  gap={3}
                  sx={{
                    "& > *": {
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 calc(50% - 12px)",
                        md: "1 1 calc(33.333% - 16px)",
                        lg: "1 1 calc(20% - 19.2px)",
                      },
                      minWidth: {
                        xs: "100%",
                        sm: "calc(50% - 12px)",
                        md: "calc(33.333% - 16px)",
                        lg: "calc(20% - 19.2px)",
                      },
                      maxWidth: {
                        xs: "100%",
                        sm: "calc(50% - 12px)",
                        md: "calc(33.333% - 16px)",
                        lg: "calc(20% - 19.2px)",
                      },
                    },
                  }}
                >
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        Available Balance
                      </MDTypography>
                      <Icon sx={{ color: "white", fontSize: "2rem" }}>account_balance_wallet</Icon>
                    </MDBox>
                    <MDTypography variant="h4" fontWeight="bold" color="white">
                      {wallet ? formatCurrency(wallet.available_balance || 0) : "EGP 0"}
                    </MDTypography>
                    <MDTypography variant="caption" color="rgba(255,255,255,0.8)" mt={1}>
                      Net earnings (after fees & deductions)
                    </MDTypography>
                  </Card>

                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      color: "white",
                      boxShadow: "0 8px 24px rgba(245, 158, 11, 0.3)",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        Pending Balance
                      </MDTypography>
                      <Icon sx={{ color: "white", fontSize: "2rem" }}>schedule</Icon>
                    </MDBox>
                    <MDTypography variant="h4" fontWeight="bold" color="white">
                      {wallet ? formatCurrency(wallet.pending_balance || 0) : "EGP 0"}
                    </MDTypography>
                    <MDTypography variant="caption" color="rgba(255,255,255,0.8)" mt={1}>
                      Awaiting checkout (after fees)
                    </MDTypography>
                  </Card>

                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                      color: "white",
                      boxShadow: "0 8px 24px rgba(79, 172, 254, 0.3)",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        Total Earned
                      </MDTypography>
                      <Icon sx={{ color: "white", fontSize: "2rem" }}>trending_up</Icon>
                    </MDBox>
                    <MDTypography variant="h4" fontWeight="bold" color="white">
                      {wallet ? formatCurrency(wallet.total_earned || 0) : "EGP 0"}
                    </MDTypography>
                    <MDTypography variant="caption" color="rgba(255,255,255,0.8)" mt={1}>
                      Gross revenue (before fees)
                    </MDTypography>
                  </Card>

                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                      color: "white",
                      boxShadow: "0 8px 24px rgba(250, 112, 154, 0.3)",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        Platform Fees
                      </MDTypography>
                      <Icon sx={{ color: "white", fontSize: "2rem" }}>percent</Icon>
                    </MDBox>
                    <MDTypography variant="h4" fontWeight="bold" color="white">
                      {wallet ? formatCurrency(wallet.platform_fees || 0) : "EGP 0"}
                    </MDTypography>
                    <MDTypography variant="caption" color="rgba(255,255,255,0.8)" mt={1}>
                      Total fees paid
                    </MDTypography>
                  </Card>

                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: "linear-gradient(135deg, #ef4444 0%, #fb7185 100%)",
                      color: "white",
                      boxShadow: "0 8px 24px rgba(239, 68, 68, 0.25)",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        Total Deductions
                      </MDTypography>
                      <Icon sx={{ color: "white", fontSize: "2rem" }}>remove_circle</Icon>
                    </MDBox>
                    <MDTypography variant="h4" fontWeight="bold" color="white">
                      {wallet ? formatCurrency(wallet.total_deductions || 0) : "EGP 0"}
                    </MDTypography>
                    <MDTypography variant="caption" color="rgba(255,255,255,0.8)" mt={1}>
                      services fees deductions
                    </MDTypography>
                  </Card>
                </MDBox>
              </Grid>

              {/* Revenue Analytics */}
              <Grid item xs={12} lg={8}>
                <Card sx={{ p: 3, borderRadius: 3 }}>
                  <MDTypography variant="h6" fontWeight="bold" mb={3}>
                    Monthly Revenue (Last 6 Months)
                  </MDTypography>
                  {monthlyRevenue.length > 0 ? (
                    <MDBox sx={{ height: "300px", position: "relative" }}>
                      <Line
                        data={{
                          labels: monthlyRevenue.map((item) => item.month),
                          datasets: [
                            {
                              label: "Revenue",
                              data: monthlyRevenue.map((item) => item.revenue),
                              borderColor: "#1976d2",
                              backgroundColor: "rgba(25, 118, 210, 0.1)",
                              fill: true,
                              tension: 0.4,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: true,
                              position: "top",
                            },
                            tooltip: {
                              callbacks: {
                                label: function (context) {
                                  return `Revenue: ${formatCurrency(context.parsed.y)}`;
                                },
                              },
                            },
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function (value) {
                                  return formatCurrency(value);
                                },
                              },
                            },
                          },
                        }}
                      />
                    </MDBox>
                  ) : (
                    <MDBox
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      py={8}
                    >
                      <Icon sx={{ fontSize: "4rem", color: "text.secondary", mb: 2 }}>
                        show_chart
                      </Icon>
                      <MDTypography variant="h6" color="text.secondary" mb={1}>
                        No Revenue Data
                      </MDTypography>
                      <MDTypography variant="body2" color="text.secondary">
                        Revenue data will appear here once you have completed bookings
                      </MDTypography>
                    </MDBox>
                  )}
                </Card>
              </Grid>

              {/* Summary Stats */}
              <Grid item xs={12} lg={4}>
                <Card sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                  <MDTypography variant="h6" fontWeight="bold" mb={3}>
                    Financial Summary
                  </MDTypography>
                  <MDBox>
                    <MDBox mb={2}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="medium"
                        display="block"
                        mb={0.5}
                      >
                        Net Earnings
                      </MDTypography>
                      <MDTypography variant="h5" fontWeight="bold" color="success.main">
                        {wallet ? formatCurrency(wallet.available_balance || 0) : "EGP 0"}
                      </MDTypography>
                    </MDBox>
                    <MDBox mt={3} p={2} sx={{ bgcolor: "info.lighter", borderRadius: 2 }}>
                      <MDTypography variant="caption" color="text.secondary" display="block" mb={1}>
                        <Icon sx={{ fontSize: "1rem", verticalAlign: "middle", mr: 0.5 }}>
                          info
                        </Icon>
                        Note
                      </MDTypography>
                      <MDTypography variant="body2" color="text.secondary">
                        Wallet balances are read-only. All financial calculations are performed by
                        the system.
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Card sx={{ p: 4, borderRadius: 3 }}>
                <MDBox display="flex" alignItems="center" gap={2}>
                  <Icon sx={{ fontSize: "2.5rem", color: "text.secondary" }}>
                    account_balance_wallet
                  </Icon>
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="bold">
                      Wallet will appear after your first reservation
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Once a client books any of your properties, your wallet balances and analytics
                      will show here.
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Transaction Table */}
          <Grid item xs={12}>
            <Card sx={{ p: 3, borderRadius: 3 }}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
                flexWrap="wrap"
                gap={2}
              >
                <MDTypography variant="h6" fontWeight="bold">
                  Transaction History
                </MDTypography>
                <MDBox display="flex" gap={2} flexWrap="wrap">
                  <TextField
                    type="date"
                    label="Start Date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ minWidth: 150 }}
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ minWidth: 150 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Property</InputLabel>
                    <Select
                      value={filters.propertyId}
                      label="Property"
                      onChange={(e) => setFilters({ ...filters, propertyId: e.target.value })}
                    >
                      <MenuItem value="all">All Properties</MenuItem>
                      {properties.map((property) => (
                        <MenuItem
                          key={property._id || property.id}
                          value={property._id || property.id}
                        >
                          {property.title?.en || property.title || "N/A"}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filters.type}
                      label="Type"
                      onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="revenue">Revenue</MenuItem>
                      <MenuItem value="fee">Platform Fee</MenuItem>
                      <MenuItem value="deduction">Deduction</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>
              </MDBox>

              {transactionsLoading ? (
                <MDBox display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </MDBox>
              ) : transactions.length === 0 ? (
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={8}
                >
                  <Icon sx={{ fontSize: "4rem", color: "text.secondary", mb: 2 }}>
                    receipt_long
                  </Icon>
                  <MDTypography variant="h6" color="text.secondary" mb={1}>
                    No Transactions Found
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" textAlign="center">
                    Transaction history will appear here when the Financial Team adds or deducts
                    money from your wallet
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Property</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Description</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transactions.map((transaction) => (
                          <TableRow
                            key={transaction.transaction_id}
                            hover
                            sx={{ cursor: transaction.booking_id ? "pointer" : "default" }}
                            onClick={() => {
                              if (transaction.booking_id) {
                                navigate(`/property-owner/reservations/${transaction.booking_id}`);
                              }
                            }}
                          >
                            <TableCell>{formatDate(transaction.created_at)}</TableCell>
                            <TableCell>
                              <MDTypography variant="body2" fontWeight="medium">
                                {transaction.property_name}
                              </MDTypography>
                              {transaction.reservation_number && (
                                <MDTypography variant="caption" color="text.secondary">
                                  {transaction.reservation_number}
                                </MDTypography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={transaction.type.toUpperCase()}
                                color={getTypeColor(transaction.type)}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <MDTypography
                                variant="body2"
                                fontWeight="bold"
                                color={transaction.amount >= 0 ? "success.main" : "error.main"}
                              >
                                {transaction.amount >= 0 ? "+" : ""}
                                {formatCurrency(transaction.amount)}
                              </MDTypography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={transaction.status.toUpperCase()}
                                color={getStatusColor(transaction.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <MDTypography variant="body2" color="text.secondary">
                                {transaction.description}
                              </MDTypography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={totalTransactions}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <PropertyOwnerFooter />
    </DashboardLayout>
  );
}

export default WalletDashboard;
